import { Injectable, inject } from '@angular/core';
import { AuthSessionService } from './auth-session.service';
import { AppStorageService } from './app-storage.service';
import { UserApiService } from './user-api.service';
import { User } from '@models/user/user';
import { BehaviorSubject, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppService {
  private storage = inject(AppStorageService);
  private authSession = inject(AuthSessionService);
  private userApi = inject(UserApiService);

  private activeUser!: User;
  private allUsers: User[] = [];

  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  currentUser: User | null = null;
  deviceType?: string;

  constructor() {
    this.detectDevice();
    this.loadCurrentUser();
  }

  // ================= USER =================

  loadCurrentUser() {
    const user = this.getActiveUser();
    if (user?.id) {
      this.userApi.getUserByID(+user.id).subscribe((res) => {
        this.currentUser = res.data;
        this.userSubject.next(res.data);
      });
    }
  }

  refreshUserData(user: User) {
    this.activeUser = user;
    this.currentUser = user;
    this.storage.saveUser(user);
    this.userSubject.next(user);
  }

  getActiveUser(): User | undefined {
    return this.activeUser || this.storage.getUser();
  }

  getUserFullName(): string {
    return `${this.currentUser?.firstname ?? ''} ${this.currentUser?.lastname ?? ''}`;
  }

  // ================= AUTH =================

  signIn(data: { user: User; token: string }) {
    this.activeUser = data.user;
    // Save user first so AppStorageService can namespace the token key by user id
    this.storage.saveUser(data.user);
    // Save token using the direct localStorage key (not user-namespaced) for auth
    this.authSession.saveToken(data.token);
    this.currentUser = data.user;
    this.userSubject.next(data.user);
  }

  signOut() {
    this.authSession.clearSession();
    this.currentUser = null;
    this.userSubject.next(null);
    this.activeUser = undefined as any;
  }

  isLoggedIn(): boolean {
    return !!this.getActiveUser();
  }

  isAdminLoggedIn(): boolean {
    const user = (this.currentUser || this.getActiveUser()) as any;
    if (!user) return false;
    if (user.is_admin === true || user.is_admin === 1) return true;

    const roles = user.roles;
    if (Array.isArray(roles)) {
      return roles.some((r: any) => {
        const name = typeof r === 'string' ? r : r?.name;
        return typeof name === 'string' && name.toUpperCase() === 'ADMIN';
      });
    }
    const role = user.role;
    if (typeof role === 'string' && role.toUpperCase() === 'ADMIN') return true;
    return false;
  }

  isAdmin(): boolean {
    return this.isAdminLoggedIn();
  }

  // ================= USERS =================

  setAllUsers(users: User[]) {
    this.allUsers = users || [];
  }

  getAllUsers(): User[] {
    return this.allUsers;
  }

  // ================= DEVICE =================

  detectDevice() {
    this.deviceType =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      )
        ? 'Mobile'
        : 'Desktop';
  }

  updateUser(data: any) {
    return this.userApi.updateUser(data).pipe(
      map((res) => {
        if (res.success) {
          this.refreshUserData(res.data);
          return res.data;
        }
        throw new Error('Update failed');
      }),
    );
  }

  updatePassword(data: any) {
    return this.userApi.changePassword(data).pipe(
      map((res) => {
        if (res.success) return true;
        throw new Error('Password update failed');
      }),
    );
  }
}
